<script setup>
import { ref } from "vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";

defineProps({
    list: {
        type: Object,
        required: true,
    },
    // Accent color behind the cover image (mockup alternates gold / lime).
    accent: {
        type: String,
        default: "#ffb545",
    },
});

const avatarFailed = ref(false);
const imageFailed = ref(false);
</script>

<template>
    <article
        class="relative flex flex-col overflow-hidden rounded-brand bg-brand-navy md:h-[325px] md:flex-row"
    >
        <!-- Accent strip: top edge on mobile, left edge on desktop -->
        <div
            aria-hidden="true"
            class="absolute inset-x-0 top-0 h-[86px] md:inset-y-0 md:left-0 md:h-auto md:w-[162px]"
            :style="{ backgroundColor: accent }"
        ></div>

        <!-- Cover image with creator chip -->
        <div class="relative mx-2.5 mt-2.5 shrink-0 md:m-[17px]">
            <div
                class="relative h-[150px] overflow-hidden rounded-[4px] bg-brand-navy/40 md:h-[288px] md:w-[291px]"
            >
                <img
                    v-if="list.image && !imageFailed"
                    :src="list.image"
                    :alt="list.title"
                    class="size-full object-cover"
                    @error="imageFailed = true"
                />
                <div
                    aria-hidden="true"
                    class="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent"
                ></div>
            </div>

            <div class="absolute bottom-3 left-3 flex items-center gap-2.5">
                <span
                    class="size-[25px] shrink-0 overflow-hidden rounded-[2px] bg-white/30"
                >
                    <img
                        v-if="list.avatar && !avatarFailed"
                        :src="list.avatar"
                        :alt="list.creator"
                        class="size-full rounded-[2px] object-cover"
                        @error="avatarFailed = true"
                    />
                </span>
                <span class="font-brand text-[14px] font-medium text-white">
                    {{ list.creator }}
                </span>
            </div>
        </div>

        <!--
            Copy + CTA; the feature cards carry no description (GH-1427).
            The column may shrink (min-w-0) and the title breaks
            inside it, so a long word wraps rather than clipping at the card
            edge; the side-by-side pair (1440px up) runs a little tighter so
            both cards fit.
        -->
        <div
            class="relative flex min-w-0 flex-1 flex-col px-[27px] pb-7 pt-5 md:justify-center md:py-8 md:pl-9 md:pr-10 min-[1440px]:pr-6"
        >
            <p
                class="font-brand-display text-[16px] font-semibold italic uppercase leading-none text-[#b1a7a5]"
            >
                Newly Added
            </p>
            <h3
                class="mt-2 max-w-[290px] font-brand-display text-[28px] font-black italic uppercase leading-[0.95] tracking-[-0.02em] text-white [overflow-wrap:anywhere] md:text-[35px] min-[1440px]:max-2xl:text-[30px]"
            >
                {{ list.title }}
            </h3>
            <BrandButton
                :href="list.url"
                variant="white"
                class="mt-6 h-11 w-full max-w-full px-6 text-[15px] md:w-[182px]"
            >
                View Expert List
            </BrandButton>
        </div>
    </article>
</template>
